/**
 * DictionaryCanvas.js - Dictionary Analysis Canvas Component
 * Split view canvas for displaying and editing dictionary analysis results
 */

import React, { useState } from 'react';
import { Icons } from './Icons';
import './DictionaryCanvas.css';

/**
 * Question type configuration
 */
const QUESTION_CONFIG = {
  express: {
    name: 'Expressed Desire',
    description: 'Expressed or desire to travel to a high-risk country',
    maxScore: 5,
    color: '#3b82f6', // Blue
  },
  suspected: {
    name: 'Suspected Travel',
    description: 'Suspected or implied travel to a high-risk country',
    maxScore: 5,
    color: '#f59e0b', // Amber
  },
  acknowledge: {
    name: 'Acknowledged Travel',
    description: 'Acknowledged or confirmed travel to a high-risk country',
    maxScore: 5,
    color: '#ef4444', // Red
  },
};

/**
 * Single question section component
 */
const QuestionSection = ({
  questionType,
  result,
  editData,
  onUpdate,
  isCollapsed,
  onToggleCollapse,
  isReadOnly = false,
  isApproved = false,
  isSubmitting = false,
  onApprove,
  onUnapprove,
}) => {
  const config = QUESTION_CONFIG[questionType];
  
  if (!config) return null;
  
  // Question is editable if not globally read-only and not individually approved
  const isEditable = !isReadOnly && !isApproved;
  
  const handleScoreChange = (value) => {
    if (!isEditable) return;
    const score = Math.max(0, Math.min(config.maxScore, parseInt(value) || 0));
    onUpdate(questionType, 'score', score);
  };
  
  const handleCountriesChange = (value) => {
    if (!isEditable) return;
    const countries = value.split(',').map(c => c.trim()).filter(c => c);
    onUpdate(questionType, 'detected_countries', countries);
  };
  
  const handleConfidenceChange = (value) => {
    if (!isEditable) return;
    const confidence = Math.max(0, Math.min(1, parseFloat(value) || 0));
    onUpdate(questionType, 'confidence', confidence);
  };

  const handleApproveClick = (e) => {
    e.stopPropagation(); // Don't toggle collapse
    if (onApprove) onApprove(questionType);
  };

  const handleUnapproveClick = (e) => {
    e.stopPropagation(); // Don't toggle collapse
    if (onUnapprove) onUnapprove(questionType);
  };

  return (
    <div className={`question-section ${isApproved ? 'approved' : ''}`} style={{ '--accent-color': config.color }}>
      <div 
        className="question-header"
        onClick={onToggleCollapse}
      >
        <div className="question-title">
          <span className="question-name">{config.name}</span>
          <span className="question-score">
            Score: {editData?.score ?? 0}/{config.maxScore}
          </span>
          {isApproved && (
            <span className="question-approved-badge">
              <Icons.CheckCircle />
              Approved
            </span>
          )}
        </div>
        <div className="question-header-actions">
          {/* Edit button for approved questions */}
          {isApproved && !isReadOnly && (
            <button 
              className="question-edit-btn"
              onClick={handleUnapproveClick}
              disabled={isSubmitting}
              title="Edit this question"
            >
              <Icons.Edit />
              Edit
            </button>
          )}
          <button className="collapse-btn">
            <Icons.ChevronDown />
          </button>
        </div>
      </div>
      
      {!isCollapsed && (
        <div className="question-content">
          <div className="question-description">
            {config.description}
          </div>
          
          <div className="form-group">
            <label>Score (0-{config.maxScore})</label>
            <input
              type="number"
              min="0"
              max={config.maxScore}
              value={editData?.score ?? 0}
              onChange={(e) => handleScoreChange(e.target.value)}
              className="score-input"
              disabled={!isEditable}
            />
          </div>
          
          <div className="form-group">
            <label>Confidence (0-1)</label>
            <div className="confidence-control">
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={editData?.confidence ?? 0}
                onChange={(e) => handleConfidenceChange(e.target.value)}
                className="confidence-slider"
                disabled={!isEditable}
              />
              <span className="confidence-value">
                {(editData?.confidence ?? 0).toFixed(2)}
              </span>
            </div>
          </div>
          
          <div className="form-group">
            <label>Detected Countries (comma-separated)</label>
            <input
              type="text"
              value={(editData?.detected_countries ?? []).join(', ')}
              onChange={(e) => handleCountriesChange(e.target.value)}
              placeholder="e.g., Syria, Iraq, Libya"
              className="countries-input"
              disabled={!isEditable}
            />
          </div>
          
          <div className="form-group">
            <label>Reasoning</label>
            <textarea
              value={editData?.reasoning ?? ''}
              onChange={(e) => isEditable && onUpdate(questionType, 'reasoning', e.target.value)}
              placeholder="Explain the score..."
              rows={4}
              className="reasoning-textarea"
              disabled={!isEditable}
            />
          </div>
          
          {result && (
            <div className="original-result">
              <div className="original-label">LLM Original:</div>
              <div className="original-data">
                Score: {result.score}/{result.max_score} | 
                Confidence: {result.confidence?.toFixed(2)} | 
                Intent: {result.intent_type}
              </div>
              {result.evidence_indicators?.length > 0 && (
                <div className="evidence-indicators">
                  <span className="indicators-label">Evidence:</span>
                  {result.evidence_indicators.slice(0, 5).map((ind, i) => (
                    <span key={i} className="indicator-tag">{ind}</span>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Per-question approve button */}
          {!isReadOnly && !isApproved && (
            <button 
              className="question-approve-btn"
              onClick={handleApproveClick}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="question-approve-spinner"></span>
                  Approving...
                </>
              ) : (
                <>
                  <Icons.CheckCircle />
                  Approve {config.name}
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * POI Verification Section Component
 */
const POIVerificationSection = ({ poiVerification, poiName }) => {
  if (!poiVerification) return null;
  
  const { poi_in_image, poi_is_post_author, poi_match_confidence, poi_match_source, detected_identities, faces_detected } = poiVerification;
  
  return (
    <div className="poi-verification-section">
      <div className="section-header">
        <Icons.User />
        <span>POI Verification</span>
        {poi_in_image && (
          <span className="poi-badge poi-in-image">POI in Image</span>
        )}
        {poi_is_post_author && !poi_in_image && (
          <span className="poi-badge poi-post-author">Post Author</span>
        )}
      </div>
      
      <div className="verification-details">
        <div className="detail-row">
          <span className="detail-label">Faces Detected:</span>
          <span className="detail-value">{faces_detected || 0}</span>
        </div>
        
        {poi_in_image && (
          <>
            <div className="detail-row">
              <span className="detail-label">POI Match Confidence:</span>
              <span className="detail-value">{poi_match_confidence?.toFixed(0)}%</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Match Source:</span>
              <span className="detail-value">{poi_match_source}</span>
            </div>
          </>
        )}
        
        {poi_is_post_author && !poi_in_image && (
          <div className="detail-row interpretation">
            <span className="detail-label">Interpretation:</span>
            <span className="detail-value">
              {poiName} is the post author - analyzing the post's intent
            </span>
          </div>
        )}
        
        {detected_identities && detected_identities.length > 0 && (
          <div className="detected-identities">
            <span className="detail-label">People Identified:</span>
            <div className="identity-list">
              {detected_identities.slice(0, 3).map((identity, idx) => (
                <div key={idx} className="identity-item">
                  <span className="identity-name">{identity.name}</span>
                  <span className="identity-confidence">({identity.confidence}%)</span>
                  <span className="identity-source">{identity.recognition_source}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Extracted Text Section Component
 */
const ExtractedTextSection = ({ extractedText }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  if (!extractedText || !extractedText.success) return null;
  
  const { raw_text, detected_countries, location_mentions, travel_intent_phrases, hashtags, word_count, confidence } = extractedText;
  
  return (
    <div className="extracted-text-section">
      <div 
        className="section-header clickable" 
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <Icons.FileText />
        <span>Extracted Text (Textract)</span>
        <span className="text-stats">{word_count} words | {(confidence * 100).toFixed(0)}% confidence</span>
        <button className="expand-btn">
          {isExpanded ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
        </button>
      </div>
      
      {/* Always show countries if detected - high priority */}
      {detected_countries && detected_countries.length > 0 && (
        <div className="countries-highlight">
          <span className="highlight-label">Countries in Text (High Priority):</span>
          <div className="country-tags">
            {detected_countries.map((country, idx) => (
              <span key={idx} className="country-tag priority-high">{country}</span>
            ))}
          </div>
        </div>
      )}
      
      {isExpanded && (
        <div className="text-details">
          {raw_text && (
            <div className="raw-text-container">
              <span className="detail-label">Raw Text:</span>
              <pre className="raw-text">{raw_text.length > 500 ? raw_text.substring(0, 500) + '...' : raw_text}</pre>
            </div>
          )}
          
          {hashtags && hashtags.length > 0 && (
            <div className="detail-row">
              <span className="detail-label">Hashtags:</span>
              <div className="tag-list">
                {hashtags.map((tag, idx) => (
                  <span key={idx} className="hashtag-tag">{tag}</span>
                ))}
              </div>
            </div>
          )}
          
          {location_mentions && location_mentions.length > 0 && (
            <div className="detail-row">
              <span className="detail-label">Location Mentions:</span>
              <div className="tag-list">
                {location_mentions.map((loc, idx) => (
                  <span key={idx} className="location-tag">{loc}</span>
                ))}
              </div>
            </div>
          )}
          
          {travel_intent_phrases && travel_intent_phrases.length > 0 && (
            <div className="detail-row">
              <span className="detail-label">Travel Intent Phrases:</span>
              <div className="tag-list">
                {travel_intent_phrases.map((phrase, idx) => (
                  <span key={idx} className="intent-tag">{phrase}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Main DictionaryCanvas component
 */
const DictionaryCanvas = ({
  isOpen,
  onClose,
  imageUrl,
  poiName,
  onPoiNameChange,
  onAnalyzePoi,
  results,
  edits,
  onUpdateEdit,
  onApprove,
  isLoading,
  error,
  isReadOnly = false,
  // Per-question approval props
  questionApprovals = {},
  onApproveQuestion,
  onUnapproveQuestion,
  // Enhanced analysis results
  poiVerification = null,
  extractedText = null,
}) => {
  const [collapsedSections, setCollapsedSections] = useState({});
  const [submittingQuestion, setSubmittingQuestion] = useState(null);
  
  if (!isOpen) return null;
  
  const toggleSection = (questionType) => {
    setCollapsedSections(prev => ({
      ...prev,
      [questionType]: !prev[questionType]
    }));
  };
  
  // Handle per-question approval
  const handleApproveQuestion = async (questionType) => {
    if (isReadOnly || !onApproveQuestion) return;
    
    setSubmittingQuestion(questionType);
    const result = await onApproveQuestion(questionType);
    setSubmittingQuestion(null);
    
    if (result.success) {
      // Auto-collapse the approved section
      setCollapsedSections(prev => ({
        ...prev,
        [questionType]: true
      }));
    }
  };
  
  // Handle unapprove (edit)
  const handleUnapproveQuestion = async (questionType) => {
    if (isReadOnly || !onUnapproveQuestion) return;
    
    setSubmittingQuestion(questionType);
    const result = await onUnapproveQuestion(questionType);
    setSubmittingQuestion(null);
    
    if (result.success) {
      // Expand the section for editing
      setCollapsedSections(prev => ({
        ...prev,
        [questionType]: false
      }));
    }
  };
  
  // Get results by question type for easier access
  const resultsByType = {};
  if (results) {
    results.forEach(r => {
      resultsByType[r.question_type] = r;
    });
  }
  
  // Count approved questions
  const approvedCount = Object.keys(questionApprovals).length;
  const totalQuestions = 3;

  return (
    <div className={`dictionary-canvas ${isReadOnly ? 'read-only' : ''}`}>
      <div className="canvas-header">
        {/* Approval status indicator */}
        <div className="approval-status">
          {approvedCount === totalQuestions ? (
            <div className="all-approved-badge">
              <Icons.CheckCircle />
              All Approved
            </div>
          ) : approvedCount > 0 ? (
            <div className="partial-approved-badge">
              <Icons.CheckCircle />
              {approvedCount}/{totalQuestions} Approved
            </div>
          ) : null}
        </div>
        
        <h2 className="canvas-title">
          Dictionary Analysis
          {isReadOnly && <span className="view-only-label">(View Only)</span>}
        </h2>
        
        <button className="close-btn" onClick={onClose}>
          <Icons.Close />
        </button>
      </div>
      
      <div className="canvas-body">
        {/* Image Preview Section */}
        <div className="image-section">
          {imageUrl && (
            <img 
              src={imageUrl} 
              alt="Analysis target" 
              className="preview-image"
            />
          )}
          
          <div className="poi-input-group">
            <label htmlFor="poi-name">POI Name (Optional)</label>
            <input
              id="poi-name"
              type="text"
              value={poiName}
              onChange={(e) => !isReadOnly && onPoiNameChange(e.target.value)}
              placeholder="Enter person of interest name..."
              className="poi-input"
              disabled={isReadOnly}
            />
          </div>
          
          {/* Analyze Button - hidden in read-only mode */}
          {!isReadOnly && (
            <button
              className="analyze-poi-btn"
              onClick={onAnalyzePoi}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="analyze-spinner"></span>
                  Analyzing...
                </>
              ) : (
                <>
                  <Icons.Search />
                  Analyze
                </>
              )}
            </button>
          )}
          
          {imageUrl && (
            <div className="image-url-display">
              <span className="url-label">URL:</span>
              <span className="url-text">{imageUrl}</span>
            </div>
          )}
        </div>
        
        {/* Loading State */}
        {isLoading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Analyzing image...</p>
            <p className="loading-subtext">Running text extraction, face recognition, and location analysis</p>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="error-state">
            <p>Error: {error}</p>
          </div>
        )}
        
        {/* Enhanced Analysis Results - POI Verification and Extracted Text */}
        {!isLoading && (poiVerification || extractedText) && (
          <div className="enhanced-analysis-section">
            <POIVerificationSection poiVerification={poiVerification} poiName={poiName} />
            <ExtractedTextSection extractedText={extractedText} />
          </div>
        )}
        
        {/* Questions Sections */}
        {!isLoading && edits && (
          <div className="questions-container">
            {['express', 'suspected', 'acknowledge'].map(questionType => (
              <QuestionSection
                key={questionType}
                questionType={questionType}
                result={resultsByType[questionType]}
                editData={edits[questionType]}
                onUpdate={onUpdateEdit}
                isCollapsed={collapsedSections[questionType] ?? !!questionApprovals[questionType]}
                onToggleCollapse={() => toggleSection(questionType)}
                isReadOnly={isReadOnly}
                isApproved={!!questionApprovals[questionType]}
                isSubmitting={submittingQuestion === questionType}
                onApprove={handleApproveQuestion}
                onUnapprove={handleUnapproveQuestion}
              />
            ))}
          </div>
        )}
        
        {/* Empty State */}
        {!isLoading && !error && !results && (
          <div className="empty-state">
            <Icons.Dictionary />
            <p>Enter an image URL or upload an image to start analysis</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DictionaryCanvas;

